<template>
  <div class="w-full max-w-4xl mx-auto flex flex-col items-center font-ds gap-6">
    <div class="flex flex-col items-center gap-2 text-center">
      <span class="text-xs font-bold uppercase tracking-ds-caps text-brand">
        Get started
      </span>
      <h2 class="text-3xl font-bold tracking-ds-tight text-foreground leading-tight">
        What would you like to add?
      </h2>
      <p class="max-w-xl text-base font-medium text-muted-foreground text-pretty">
        Describe it in your own words or drop a statement. Allposit classifies it,
        opens the right form, and asks only for what's missing.
      </p>
    </div>

    <AddAssetComposerField
      class="w-full"
      @describe="handleDescribe"
    />

    <div
      ref="sourcesRef"
      class="w-full flex flex-col gap-3 overflow-hidden"
    >
      <div class="flex items-center gap-3 mb-4">
        <span class="flex-1 h-px bg-border" />
        <span class="text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
          Or pick a source
        </span>
        <span class="flex-1 h-px bg-border" />
      </div>
      <div
        class="grid gap-2.5"
        :style="{ gridTemplateColumns: `repeat(${sourcesGridColumns}, 1fr)` }"
      >
        <DSButton
          v-for="source in sources"
          :key="source.label"
          type="button"
          variant="secondary"
          size="lg"
          class="justify-start"
          :data-refid="source.label"
          @click="source.action"
        >
          <component
            :is="source.icon"
            class="size-4 shrink-0 text-muted-foreground"
          />
          <span class="text-sm font-medium text-foreground">{{ source.label }}</span>
        </DSButton>
      </div>
    </div>

    <div
      v-if="syncing"
      class="flex items-center gap-2"
    >
      <Loader2 class="size-4 shrink-0 animate-spin text-muted-foreground" />
      <p class="text-sm font-medium text-muted-foreground">
        Your accounts are now being refreshed
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { markRaw, ref, useTemplateRef } from 'vue'
import {
  BanknoteIcon,
  BriefcaseIcon,
  CarIcon,
  GemIcon,
  HouseIcon,
  Loader2,
  ScrollTextIcon,
  WalletIcon,
} from '@lucide/vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import { useAddAssetFlow } from '@/hooks/addAssetFlow'
import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'
import { useModalsStore } from '@/store/modals'

import { DEFAULT_INSTITUTIONS } from '@/views/LinkedData/utils/const'

import ArchIcon from '@/assets/images/icons/arch.svg'
import CartaIcon from '@/assets/images/icons/carta.svg'

import AddAssetComposerField from '@/modules/AddAsset/components/AddAssetComposerField.vue'
import ArchAccount from '@/modules/wizards/ArchAccount/ArchAccount.vue'
import { DSButton } from '@ds'
import { useResizeObserver, useThrottleFn } from '@vueuse/core'

const CARTA_INSTITUTION = DEFAULT_INSTITUTIONS.find(
  institution => institution.institution_id === 'carta',
)

const { syncing } = useLinkedDataConnector()

const modalsStore = useModalsStore()

const { handleConnect, handleClickAddAccount, handleDescribe, handlePickFlow } = useAddAssetFlow()

const sourcesRef = useTemplateRef('sourcesRef')
const sourcesGridColumns = ref(1)

useResizeObserver(
  sourcesRef,
  entries => {
    const { width } = entries[0].contentRect
    udpateSourcesGridColumns(width)
  },
)

const udpateSourcesGridColumns = useThrottleFn((width: number) => {
  if (width > 440 && width < 720) {
    sourcesGridColumns.value = 2
  } else if (width < 480) {
    sourcesGridColumns.value = 1
  } else {
    sourcesGridColumns.value = 3
  }
}, 200)

const openArchAccount = () => {
  const modalInstance = modalsStore.init('arch-account', markRaw(ArchAccount))
  modalInstance?.open(modalsStore.getZIndex())
}

const openCarta = () => {
  if (!CARTA_INSTITUTION) return
  handleConnect(CARTA_INSTITUTION)
}

const sources = [
  {
    icon: WalletIcon,
    label: 'Connect an account',
    action: handleClickAddAccount,
  },
  {
    icon: HouseIcon,
    label: 'Real estate',
    action: () => handlePickFlow(AssetWizardFlow.REAL_ESTATE),
  },
  {
    icon: BanknoteIcon,
    label: 'Loan',
    action: () => handlePickFlow(AssetWizardFlow.LOAN),
  },
  {
    icon: BriefcaseIcon,
    label: 'Private fund',
    action: () => handlePickFlow(AssetWizardFlow.PRIVATE_FUND),
  },
  {
    icon: ScrollTextIcon,
    label: 'Private security or other',
    action: () => handlePickFlow(AssetWizardFlow.PRIVATE_SECURITY),
  },
  {
    icon: GemIcon,
    label: 'Fine art & collectables',
    action: () => handlePickFlow(AssetWizardFlow.FINE_ART),
  },
  {
    icon: CarIcon,
    label: 'Vehicle',
    action: () => handlePickFlow(AssetWizardFlow.VEHICLE),
  },
  {
    icon: ArchIcon,
    label: 'Arch account',
    action: openArchAccount,
  },
  {
    icon: CartaIcon,
    label: 'Carta account',
    action: openCarta,
  },
]
</script>
